import {
  SetStateAction,
  Dispatch,
  ChangeEvent,
  useEffect,
  useState,
} from 'react';
import {
  DevHubAlert,
  DevHubChip,
  DevHubDynamicIcon,
  DevHubFilter,
  DevHubGrid,
  DevHubIconButton,
  DevHubInputAdornment,
  DevHubMenuItem,
  DevHubPaper,
  DevHubSelect,
  DevHubTextField,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import { UserRole } from '../types/RoleTypes';

type Props = {
  setSearchResults: Dispatch<SetStateAction<UserRole[] | undefined>>;
  searchResults: any;
  selectedRoles: string[];
  handleRoleChange: (role: string) => void;
  roleList: any;
  viewByRole: boolean;
  onViewModeChange: (viewByRole: boolean) => void;
};

/**
 * RoleFilter searches for devices belonging to an application
 * @param props Props of the component
 * @param props.setApps Set applications on parent to search results
 */
const RoleFilter = (props: Props) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchExecuted, setSearchExecuted] = useState(false);
  const [roleItems, setRoleItems] = useState<string[]>([]);

  const handleSearch = (searchInput: string) => {
    const results: UserRole[] = [];

    props?.roleList?.forEach((obj: UserRole) => {
      const { firstName, lastName, login, name } = JSON.parse(obj.party);
      const roles = obj.roles;

      if (
        firstName?.toLowerCase().includes(searchInput.toLowerCase()) ||
        lastName?.toLowerCase().includes(searchInput.toLowerCase()) ||
        login?.toLowerCase().includes(searchInput.toLowerCase()) ||
        name?.toLowerCase().includes(searchInput.toLowerCase()) ||
        roles.some(role =>
          role?.displayName?.toLowerCase().includes(searchInput.toLowerCase()),
        )
      ) {
        results.push(obj);
      }
    });

    setSearchExecuted(true);
    props.setSearchResults(results);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setSearchExecuted(false);
    props.setSearchResults(undefined);
  };

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    if (value?.length <= 0) {
      clearSearch();
      return;
    }

    if (value.length >= 3 && value.length - searchTerm.length > 0) {
      handleSearch(value);
    }
    setSearchTerm(value);
  };

  const renderAlertMessage = () => {
    const totalCount = props?.searchResults?.length || 0;
    const resultsText = totalCount === 1 ? 'result' : 'results';

    return `Showing ${totalCount} ${resultsText} for "${searchTerm}"`;
  };

  useEffect(() => {
    if (props?.roleList) {
      const filterItems: UserRole[] =
        props?.searchResults?.length > 0
          ? props?.searchResults
          : props?.roleList;

      const combinedRoles = filterItems?.flatMap((role: UserRole) =>
        role.roles.map((r: any) => r.displayName),
      );

      const uniqueRoles = [...new Set(combinedRoles)];

      setRoleItems(uniqueRoles?.sort());
    }
  }, [props.roleList, props.searchResults]);

  return (
    <>
      <DevHubPaper>
        <DevHubGrid container alignItems="center" spacing={1}>
          <DevHubGrid item xs={12} sm={12} md={6} lg={4}>
            <DevHubTextField
              id="role-search"
              label="Search Roles"
              margin="normal"
              variant="outlined"
              noMargin
              onChange={onChange}
              value={searchTerm}
              size="small"
              InputProps={{
                endAdornment: (
                  <DevHubInputAdornment position="end">
                    <DevHubIconButton onClick={clearSearch}>
                      <DevHubDynamicIcon iconName="Clear" />
                    </DevHubIconButton>
                  </DevHubInputAdornment>
                ),
              }}
            />
          </DevHubGrid>
          <DevHubGrid item>
            <DevHubFilter
              title="Roles"
              list={roleItems}
              handleChange={props.handleRoleChange}
              selectedItems={props.selectedRoles}
            />
          </DevHubGrid>
          {props.selectedRoles.length > 0 && (
            <>
              {props.selectedRoles.map((status: string, index: number) => (
                <DevHubGrid item key={index}>
                  <DevHubChip
                    noMargins
                    label={status}
                    onDelete={() => props.handleRoleChange(status)}
                  />
                </DevHubGrid>
              ))}
            </>
          )}
          <DevHubGrid item style={{ marginLeft: 'auto' }}>
            <DevHubGrid container alignItems="center" spacing={1}>
              <DevHubGrid item>
                <DevHubTypography variant="body2">View By:</DevHubTypography>
              </DevHubGrid>
              <DevHubGrid item>
                <DevHubSelect
                  value={props.viewByRole ? 'role' : 'party'}
                  onChange={(e: React.ChangeEvent<{ value: unknown }>) => {
                    props.onViewModeChange(e.target.value === 'role');
                  }}
                  variant="outlined"
                  SelectDisplayProps={{
                    style: { paddingTop: '8px', paddingBottom: '8px' },
                  }}
                >
                  <DevHubMenuItem value="party">Party</DevHubMenuItem>
                  <DevHubMenuItem value="role">Role</DevHubMenuItem>
                </DevHubSelect>
              </DevHubGrid>
            </DevHubGrid>
          </DevHubGrid>
        </DevHubGrid>
        {searchExecuted &&
          (props?.searchResults?.length === 0 ? (
            <DevHubGrid item xs={12}>
              <DevHubAlert variant="outlined" severity="error">
                No results found for "{searchTerm}"
              </DevHubAlert>
            </DevHubGrid>
          ) : (
            <DevHubGrid item xs={12}>
              <DevHubAlert variant="outlined" severity="success">
                {renderAlertMessage()}
              </DevHubAlert>
            </DevHubGrid>
          ))}
      </DevHubPaper>
    </>
  );
};

export default RoleFilter;
